import { useCallback } from 'react';
import type {
  CalendarEventType,
  CalendarComponentEvent,
  CalendarPermissions,
} from '@axiom/validation';

import { CalendarEventsUtil } from './calendar-events-util';
import { EVENT_STATES } from './calendar-events.const';
import type { UpdateEventArgType } from './RawCalendar';

type UseCalendarEventHandlersParams = {
  editableEventId?: string | null;
  perms: CalendarPermissions;
  bgEventIdsSet: Set<CalendarEventType['id']>;
  mergeEvents: (events: CalendarComponentEvent[]) => CalendarComponentEvent[];
  eventDataRef: React.MutableRefObject<CalendarComponentEvent[]>;
  backgroundEventDataRef: React.MutableRefObject<CalendarComponentEvent[]>;
  setEventData: React.Dispatch<React.SetStateAction<CalendarComponentEvent[]>>;
  selectedEvent: CalendarComponentEvent | null;
  setSelectedEvent: React.Dispatch<
    React.SetStateAction<CalendarComponentEvent | null>
  >;
};

type UseCalendarEventHandlersReturn = {
  handleSelectSlot: (props: {
    start: Date;
    end: Date;
    action: 'select' | 'click' | 'doubleClick';
  }) => null;
  updateEvent: (args: UpdateEventArgType) => void;
  onDeleteHandler: () => void;
};

export const useCalendarEventHandlers = ({
  editableEventId,
  perms,
  bgEventIdsSet,
  mergeEvents,
  eventDataRef,
  backgroundEventDataRef,
  setEventData,
  selectedEvent,
  setSelectedEvent,
}: UseCalendarEventHandlersParams): UseCalendarEventHandlersReturn => {
  const updateEvent = useCallback(
    ({ event, start, end }: UpdateEventArgType) => {
      setEventData(prevState => {
        const adjustedEnd = CalendarEventsUtil.applyDurationFloor(
          event.busy,
          start,
          end
        );
        // Free-time drag/resize that overhangs a busy event: replace the
        // dragged event in state with the trimmed pieces so the user sees
        // the carved-around result immediately. Edit mode only edits
        // interviews (busy=true), so the editable event itself never lands
        // here — no edit-mode guard needed.
        if (!event.busy) {
          const busyEvents = [
            ...prevState.filter(e => e.busy && e.id !== event.id),
            ...backgroundEventDataRef.current.filter(e => e.busy),
          ];
          const overlapsBusy = busyEvents.some(
            b =>
              b.start.getTime() < adjustedEnd.getTime() &&
              b.end.getTime() > start.getTime()
          );
          if (overlapsBusy) {
            // Pieces produced by the split are net-new rows from the BE's POV,
            // even when the original was a SAVED event. They get fresh
            // UNSAVED_<…> ids that don't map to any DB row, so marking them
            // MODIFIED would send a PATCH for a row that doesn't exist. The
            // original (if SAVED) is dropped from state below and will be
            // captured as a delete by the form's diff.
            const updated: CalendarComponentEvent = {
              ...event,
              error: null,
              start,
              end: adjustedEnd,
              state: EVENT_STATES.NEW,
            };
            const withoutOriginal = prevState
              .filter(e => e.id !== event.id)
              .map(e => ({ ...e, error: null }));
            const pieces = CalendarEventsUtil.splitFreeTimeAroundBusy(
              updated,
              busyEvents
            );
            const toBeMerged = [...withoutOriginal, ...pieces];

            if (backgroundEventDataRef.current.length > 0) {
              return CalendarEventsUtil.setEventError(
                {
                  foreground: mergeEvents(toBeMerged),
                  background: backgroundEventDataRef.current,
                },
                perms,
                editableEventId
              ).filter(data => !bgEventIdsSet.has(data.id));
            }

            return CalendarEventsUtil.setEventError(
              { foreground: mergeEvents(toBeMerged) },
              perms,
              editableEventId
            );
          }
        }

        const toBeMerged = prevState.map<CalendarComponentEvent>(calEvent => {
          if (calEvent.id !== event.id) {
            return { ...calEvent, error: null };
          }
          return {
            ...calEvent,
            error: null,
            start,
            end: adjustedEnd,
            state:
              calEvent.state === EVENT_STATES.SAVED
                ? EVENT_STATES.MODIFIED
                : calEvent.state,
          };
        });

        const eventExists = toBeMerged.some(e => e.id === event.id);
        if (!eventExists) {
          toBeMerged.push(event);
        }

        if (backgroundEventDataRef.current.length > 0) {
          // need the background events for error checking only
          return CalendarEventsUtil.setEventError(
            {
              foreground: mergeEvents(toBeMerged),
              background: backgroundEventDataRef.current,
            },
            perms,
            editableEventId
          ).filter(data => !bgEventIdsSet.has(data.id));
        }

        return CalendarEventsUtil.setEventError(
          { foreground: mergeEvents(toBeMerged) },
          perms,
          editableEventId
        );
      });
    },
    [
      perms,
      bgEventIdsSet,
      mergeEvents,
      backgroundEventDataRef,
      setEventData,
      editableEventId,
    ]
  );

  const handleSelectSlot = useCallback(
    (props: {
      start: Date;
      end: Date;
      action: 'select' | 'click' | 'doubleClick';
    }): null => {
      // A click (or double-click) that lands on top of a busy background
      // event (i.e. a scheduled interview rendered as a non-interactive
      // background in AVAILABILITY mode) should not create an overlapping
      // foreground event. Surface the interview through the same
      // CalendarDeleteEventFlow modal used during edit-interview-time —
      // delete is hidden because background events are read-only here.
      // Drag-to-select (action === 'select') is not intercepted; it goes
      // through the normal create flow so the backend can split the new
      // free-time block around the interview.
      if (props.action === 'click' || props.action === 'doubleClick') {
        const clickedTime = props.start.getTime();
        const hit = backgroundEventDataRef.current.find(
          e =>
            e.busy &&
            clickedTime >= e.start.getTime() &&
            clickedTime < e.end.getTime()
        );
        if (hit) {
          setSelectedEvent({ ...hit, isBackgroundEvent: true });
          return null;
        }
      }

      if (editableEventId != null) {
        // Edit mode: clicking/drag-selecting a slot moves the editable
        // event to that start time, preserving its original duration.
        // This unlocks cross-week moves (drag is constrained to the
        // visible view) — navigate to any week, click, the event jumps.
        const editable = eventDataRef.current.find(
          e => e.id === editableEventId
        );
        if (!editable) return null;
        const durationMs = editable.end.getTime() - editable.start.getTime();
        const newStart = props.start;
        const newEnd = new Date(newStart.getTime() + durationMs);
        updateEvent({ event: editable, start: newStart, end: newEnd });
        return null;
      }

      // Step 1: Check permissions
      if (!CalendarEventsUtil.canCreateSlotEvent(perms, props.action)) {
        return null;
      }

      // Step 2: Create new event
      const newEvent = CalendarEventsUtil.createNewSlotEvent({
        start: props.start,
        end: props.end,
        action: props.action,
        perms,
      });

      // Step 3: Validate event can be created
      if (newEvent.busy) {
        // No-op when the new interview would start in the unavailable
        // (past / same-day lead-time) region — the same rule the greyed
        // slotPropGetter overlay paints, and the inverse of canAddEvent's
        // gate for free-time below. Without this, a click (which bypasses
        // onSelecting) drops an interview into the gray, and it's only
        // caught later at save time.
        if (CalendarEventsUtil.isInThePast(newEvent.start, perms)) {
          return null;
        }

        const dataEvents = CalendarEventsUtil.selectEventsForValidation(
          backgroundEventDataRef.current,
          eventDataRef.current
        );

        if (
          !CalendarEventsUtil.canCreateStaticEvent({
            newEvent,
            existingEvents: dataEvents,
            perms,
          })
        ) {
          return null;
        }
      } else if (
        !CalendarEventsUtil.canAddEvent(perms, newEvent.start.toISOString())
      ) {
        return null;
      }

      // Step 4: Add to state
      setEventData(prevState => {
        const processedState =
          CalendarEventsUtil.removeUnsavedStaticEventIfNeeded(prevState, perms);

        // New free-time block that overhangs any existing busy event gets
        // split into pieces so the user sees the carved-around result
        // immediately. Backend does the same trim on save. Skip the splitter
        // in the common no-overlap case so the event keeps its identity.
        if (newEvent.busy) {
          processedState.push(newEvent);
        } else {
          const busyEvents = [
            ...prevState.filter(e => e.busy),
            ...backgroundEventDataRef.current.filter(e => e.busy),
          ];
          const overlapsBusy = busyEvents.some(
            b =>
              b.start.getTime() < newEvent.end.getTime() &&
              b.end.getTime() > newEvent.start.getTime()
          );
          if (overlapsBusy) {
            processedState.push(
              ...CalendarEventsUtil.splitFreeTimeAroundBusy(
                newEvent,
                busyEvents
              )
            );
          } else {
            processedState.push(newEvent);
          }
        }

        return CalendarEventsUtil.validateAndFilterEvents({
          events: processedState,
          backgroundEvents: backgroundEventDataRef.current,
          perms,
          bgEventIdsSet,
          mergeEvents,
          editableEventId,
        });
      });

      return null;
    },
    [
      editableEventId,
      perms,
      bgEventIdsSet,
      mergeEvents,
      eventDataRef,
      backgroundEventDataRef,
      setEventData,
      setSelectedEvent,
      updateEvent,
    ]
  );

  const onDeleteHandler = useCallback(() => {
    setEventData(prevState => {
      return prevState.filter(ev => ev.id !== selectedEvent?.id);
    });

    setSelectedEvent(null);
  }, [selectedEvent, setEventData, setSelectedEvent]);

  return {
    handleSelectSlot,
    updateEvent,
    onDeleteHandler,
  };
};
